import BaseDetailView from '../shared/BaseDetailView';
import Badge from '../shared/Badge';
import EditableDetailPage from '../shared/EditableDetailPage';
import DetailSection from '../shared/DetailSection';
import InfoField from '../shared/InfoField';
import FieldGrid from '../shared/FieldGrid';
import DetailViewActionButtons from '../shared/DetailViewActionButtons';
import toast from 'react-hot-toast';
import { ICONS } from '../../constants/detailViewConstants';
import { useDetailViewActions } from '../../hooks/useDetailViewActions.jsx';
import { getSubTheme } from '../../constants/subThemes';

export default function InvitationDetailView({ invitation, onDelete, theme = 'purple' }) {
  const themeConfig = getSubTheme(theme);
  const { createDeleteAction } = useDetailViewActions();
  if (!invitation) {
    return (
      <BaseDetailView
        item={null}
        emptyStateIcon={ICONS.user}
        emptyStateTitle="Select an invitation to view details"
        headerContent={null}
        headerActions={null}
        tabs={[{ id: 'details', label: 'Details', icon: ICONS.details }]}
        defaultTab="details"
        renderContent={() => null}
      />
    );
  }

  const copyInviteLink = () => {
    const inviteUrl = `${window.location.origin}/register?token=${invitation.token}`;
    navigator.clipboard.writeText(inviteUrl);
    toast.success('Invitation link copied to clipboard!');
  };

  const inviteUrl = `${window.location.origin}/register?token=${invitation.token}`;
  const expiresDate = new Date(invitation.expires_at);

  // Header content with invitation icon and badges
  const headerContent = (
    <>
      <div className="flex items-center gap-4 mb-2">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center" style={{ color: 'var(--color-text-on-accent)' }}>
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        </div>
        <div>
          <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Pending Invitation</h2>
          <div className="mt-2">
            <Badge variant="gray">
              expires: {expiresDate.toLocaleDateString()} at {expiresDate.toLocaleTimeString()}
            </Badge>
          </div>
        </div>
      </div>
    </>
  );

  // Render content
  const renderContent = (activeTab) => {
    if (activeTab === 'details') {
      return (
        <EditableDetailPage
          title="Invitation Details"
          subtitle="View invitation link and details"
          isEditMode={false}
        >
          {/* Invitation Link Section */}
          <DetailSection title="Invitation Link">
            <div className="space-y-4">
              <div className="relative">
                <input
                  type="text"
                  value={inviteUrl}
                  readOnly
                  className={`w-full pr-12 pl-4 py-3.5 bg-gradient-to-r from-gray-900 to-gray-800 border-2 border-${theme}-500/30 rounded-xl text-gray-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-${theme}-500/50 ${themeConfig.focus} shadow-lg shadow-${theme}-900/10 overflow-hidden transition-all`}
                  style={{ color: 'var(--color-text-on-accent)' }}
                />
                <button
                  onClick={copyInviteLink}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-${theme}-400 transition-colors`}
                  title="Copy to clipboard"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
              <p className="text-xs text-gray-400">
                Copy and share this link with the person you want to invite
              </p>
            </div>
          </DetailSection>

          {/* Expiration Details Section */}
          <DetailSection title="Expiration">
            <FieldGrid columns={2}>
              <InfoField label="Expires On" value={expiresDate.toLocaleDateString()} />
              <InfoField label="Expires At" value={expiresDate.toLocaleTimeString()} />
              <InfoField
                label="Status"
                value={
                  <Badge variant="warning">
                    pending
                  </Badge>
                }
              />
            </FieldGrid>
          </DetailSection>

          {/* Assigned Roles Section */}
          {invitation.roles && invitation.roles.length > 0 && (
            <DetailSection title="Assigned Roles">
              <div className="flex flex-wrap gap-2">
                {invitation.roles.map((role) => (
                  <Badge key={role.role_id} variant="primary">
                    {role.role_name}
                  </Badge>
                ))}
              </div>
            </DetailSection>
          )}

          {/* Product Access Section */}
          {invitation.products && invitation.products.length > 0 && (
            <DetailSection title="Product Access">
              <FieldGrid columns={1}>
                {invitation.products.map((product) => (
                  <div key={product.product_id} className="flex items-center justify-between py-2 border-b border-gray-700/50 last:border-0">
                    <span className="text-white">{product.product_name}</span>
                    <Badge variant="info">
                      {product.access_level}
                    </Badge>
                  </div>
                ))}
              </FieldGrid>
            </DetailSection>
          )}

          {/* Notes Section */}
          {invitation.notes && (
            <DetailSection title="Notes">
              <p className="text-gray-300">{invitation.notes}</p>
            </DetailSection>
          )}
        </EditableDetailPage>
      );
    }
    return null;
  };

  // Header actions
  const headerActions = invitation && onDelete ? [
    createDeleteAction(
      () => onDelete(invitation.id),
      'Are you sure you want to delete this invitation? This action cannot be undone.',
      'Delete invitation'
    )
  ] : [];

  return (
    <BaseDetailView
      item={invitation}
      emptyStateIcon={ICONS.user}
      emptyStateTitle="No invitation selected"
      emptyStateSubtitle="Select an invitation from the sidebar to view details"
      headerContent={headerContent}
      headerActions={<DetailViewActionButtons actions={headerActions} />}
      tabs={[{ id: 'details', label: 'Details', icon: ICONS.details }]}
      defaultTab="details"
      renderContent={renderContent}
    />
  );
}
